import {
  type AnyRuleType,
  StyleRule,
  MediaRule,
} from '@packages/systems/style/style-sheets';
import {
  type LonghandId,
  LONGHAND_TO_CSS_PROPERTY,
} from '@packages/systems/style/properties';
import {reportBug} from '@packages/utilities/reporter';
/*:: import type {ValueType} from '@packages/systems/style/properties'; */
import type {CSSWriter} from './types';

const ADD_STYLE_RULE = 'ADD_STYLE_RULE';
const ADD_MEDIA_RULE = 'ADD_MEDIA_RULE';
const SET_PROPERTY = 'SET_PROPERTY';
const REMOVE_PROPERTY = 'REMOVE_PROPERTY';
const ADD_STYLE_RULE_TO_PARENT_RULE = 'ADD_STYLE_RULE_TO_PARENT_RULE';
const REMOVE_RULE = 'REMOVE_RULE';
const REMOVE_STYLE_RULE_FROM_PARENT_RULE = 'REMOVE_STYLE_RULE_FROM_PARENT_RULE';
const SET_MEDIA_QUERIES = 'SET_MEDIA_QUERIES';
const CLEAR = 'CLEAR';

type UpdateType =
  | {
      type: typeof ADD_STYLE_RULE;
      args: [StyleRule];
    }
  | {
      type: typeof ADD_MEDIA_RULE;
      args: [MediaRule];
    }
  | {
      type: typeof SET_PROPERTY;
      args: [StyleRule, LonghandId];
    }
  | {
      type: typeof REMOVE_PROPERTY;
      args: [StyleRule, LonghandId];
    }
  | {
      type: typeof ADD_STYLE_RULE_TO_PARENT_RULE;
      args: [StyleRule, MediaRule];
    }
  | {
      type: typeof REMOVE_RULE;
      args: [AnyRuleType];
    }
  | {
      type: typeof REMOVE_STYLE_RULE_FROM_PARENT_RULE;
      args: [StyleRule, MediaRule];
    }
  | {
      type: typeof SET_MEDIA_QUERIES;
      args: [MediaRule];
    }
  | {
      type: typeof CLEAR;
    };

// Allows to reuse one animation frame for multiple subsequent calls.
// In the future it could calculate the remaining time in the current animation frame and
// use a new one in case there is more work.
// Evtl. we could use React's scheduler once it is stable.
class Scheduler {
  declare requestId: number | null;
  callbacks: Array<() => void> = [];
  next(callback: () => void) {
    this.callbacks.push(callback);
    if (this.requestId) {
      return;
    }
    this.requestId = requestAnimationFrame(() => {
      const {callbacks} = this;
      this.callbacks = [];
      this.requestId = null;
      for (const cb of callbacks) {
        cb();
      }
    });
  }
}

const setProperty = (
  cssRule: CSSStyleRule,
  property: LonghandId,
  value: string | number
) => {
  try {
    cssRule.style.setProperty(
      LONGHAND_TO_CSS_PROPERTY[property],
      String(value)
    );
  } catch (err: any) {
    reportBug('CSSOMWriterError', err.message, {
      team: 'platform',
      ticket: 'https://webflow.atlassian.net/browse/PLAT-1515',
      rule: {type: 'setProperty', property, value},
    });
  }
};

const removeProperty = (cssRule: CSSStyleRule, property: LonghandId) => {
  try {
    cssRule.style.removeProperty(property);
  } catch (err: any) {
    reportBug('CSSOMWriterError', err.message, {
      team: 'platform',
      ticket: 'https://webflow.atlassian.net/browse/PLAT-1515',
      rule: {type: 'removeProperty', property},
    });
  }
};

const insertRule = <CSSRuleType>(
  rule: string,
  cssParent: CSSStyleSheet | CSSMediaRule,
  index?: number
): CSSRuleType | false => {
  let insertedIndex;

  try {
    insertedIndex = cssParent.insertRule(
      rule,
      index || cssParent.cssRules.length
    );
  } catch (err: any) {
    if (!/:focus-visible/.test(rule)) {
      // ignore failures for :focus-visible parsings
      // see https://webflow.atlassian.net/browse/PDTY-2659
      reportBug('CSSOMWriterError', err.message, {
        team: 'platform',
        ticket: 'https://webflow.atlassian.net/browse/PLAT-1515',
        rule: {type: 'insertRule', rule},
      });
    }
  }

  if (insertedIndex === undefined) {
    return false;
  }
  // Flow types are wrong.
  // Cannot return cssParent.cssRules[insertedIndex] because CSSRule [1] is incompatible with
  // CSSRuleType [2].
  return cssParent.cssRules[insertedIndex] as any;
};

const deleteRule = (cssParent: CSSStyleSheet | CSSMediaRule, index: number) => {
  try {
    cssParent.deleteRule(index);
  } catch (err: any) {
    reportBug('CSSOMWriterError', err.message, {
      team: 'platform',
      ticket: 'https://webflow.atlassian.net/browse/PLAT-1515',
      rule: {type: 'deleteRule', atIndex: index},
    });
  }
};

const getRuleIndex = (
  cssRule: CSSStyleRule | CSSMediaRule,
  cssParent: CSSStyleSheet | CSSMediaRule
): number => {
  // To find a specific rule in CSSOM we need to iterate over the list.
  // To optimize this we would need to maintain our own index, but mb it's not a problem,
  // depending on how often we actually need this.
  for (let index = 0; index < cssParent.cssRules.length; index++) {
    const currCssRule = cssParent.cssRules[index];
    if (cssRule === currCssRule) {
      return index;
    }
  }
  return -1;
};

const clear = (node: HTMLStyleElement) => {
  if (!node.sheet) return;
  while (node.sheet.cssRules.length > 0) {
    deleteRule(node.sheet, 0);
  }
};

class StyleSheetRenderer {
  ownerNode: HTMLStyleElement;
  cssRules: Map<AnyRuleType, CSSStyleRule | CSSMediaRule> = new Map();
  window: WindowProxy;

  constructor(ownerNode?: HTMLStyleElement) {
    if (ownerNode) {
      this.ownerNode = ownerNode;
    } else {
      // See HTMLStyleElement type def.
      this.ownerNode = document.createElement('style') as any;
      if (document.head) document.head.appendChild(this.ownerNode);
    }
    // In designer, the `ownerNode` comes from an iframe.
    // We need to reference the right window object when using instanceof.
    // @ts-expect-error - TS2322 - Type '(Window & typeof globalThis) | null' is not assignable to type 'Window'.
    this.window = this.ownerNode.ownerDocument.defaultView;
  }

  setProperty(rule: StyleRule, property: LonghandId) {
    const cssRule = this.cssRules.get(rule);
    const value = rule.block.get(property);
    // @ts-expect-error - TS2339 - Property 'CSSStyleRule' does not exist on type 'Window'.
    if (cssRule instanceof this.window.CSSStyleRule && value != null) {
      // @ts-expect-error - TS2345 - Argument of type 'CSSStyleRule | CSSMediaRule | undefined' is not assignable to parameter of type 'CSSStyleRule'.
      setProperty(cssRule, property, value);
    }
  }

  removeProperty(rule: StyleRule, property: LonghandId) {
    const cssRule = this.cssRules.get(rule);
    // @ts-expect-error - TS2339 - Property 'CSSStyleRule' does not exist on type 'Window'.
    if (cssRule instanceof this.window.CSSStyleRule) {
      // @ts-expect-error - TS2345 - Argument of type 'CSSStyleRule | CSSMediaRule | undefined' is not assignable to parameter of type 'CSSStyleRule'.
      removeProperty(cssRule, property);
    }
  }

  addStyleRule(rule: StyleRule, mediaRule?: MediaRule) {
    const {sheet} = this.ownerNode;
    if (!sheet) return;

    let cssParent = sheet;
    if (mediaRule) {
      const cssMediaRule = this.cssRules.get(mediaRule);
      // @ts-expect-error - TS2339 - Property 'CSSMediaRule' does not exist on type 'Window'.
      if (cssMediaRule instanceof this.window.CSSMediaRule) {
        // @ts-expect-error - TS2322 - Type 'CSSStyleRule | CSSMediaRule | undefined' is not assignable to type 'CSSStyleSheet'.
        cssParent = cssMediaRule;
      }
    }

    // We could have compiled the rule to CSS strign first and inserted it in one call,
    // but it seems to be slower in Chrome and we wouldn't be able to use Typed CSSOM
    // for the initial values.
    // https://esbench.com/bench/5d779bcb4cd7e6009ef6278f

    const cssRule = insertRule<CSSStyleRule>(
      `${String(rule.selector)} {}`,
      cssParent
    );

    if (cssRule === false) {
      return;
    }
    this.cssRules.set(rule, cssRule);
    for (const [property, value] of rule.block) {
      setProperty(cssRule, property, value);
    }
  }

  removeRule(rule: AnyRuleType, mediaRule?: MediaRule) {
    const {sheet} = this.ownerNode;
    if (!sheet) return;

    let cssParent = sheet;
    if (mediaRule) {
      const cssMediaRule = this.cssRules.get(mediaRule);
      // @ts-expect-error - TS2339 - Property 'CSSMediaRule' does not exist on type 'Window'.
      if (cssMediaRule instanceof this.window.CSSMediaRule) {
        // @ts-expect-error - TS2322 - Type 'CSSStyleRule | CSSMediaRule | undefined' is not assignable to type 'CSSStyleSheet'.
        cssParent = cssMediaRule;
      }
    }

    const cssRule = this.cssRules.get(rule);
    if (cssRule) {
      const index = getRuleIndex(cssRule, cssParent);
      if (index !== -1) {
        deleteRule(cssParent, index);
      }
    }
    this.cssRules.delete(rule);
  }

  insertMediaRule(mediaRule: MediaRule, index?: number) {
    const {sheet} = this.ownerNode;
    if (!sheet) return;

    // TODO this serialization should be part of media Rule
    let queries = '';
    for (const query of mediaRule.queries) {
      if (queries) queries += ', ';
      queries += query.toCSS();
    }

    const insertedMediaRule = insertRule<CSSMediaRule>(
      `@media ${queries} {}`,
      sheet,
      index
    );

    if (insertedMediaRule === false) {
      return;
    }
    this.cssRules.set(mediaRule, insertedMediaRule);

    for (const styleRule of mediaRule.rules) {
      this.addStyleRule(styleRule, mediaRule);
    }
  }

  setMediaQueries(mediaRule: MediaRule) {
    const {sheet} = this.ownerNode;
    if (!sheet) return;

    const cssRule = this.cssRules.get(mediaRule);
    // @ts-expect-error - TS2339 - Property 'CSSMediaRule' does not exist on type 'Window'.
    if (cssRule instanceof this.window.CSSMediaRule) {
      // @ts-expect-error - TS2345 - Argument of type 'CSSStyleRule | CSSMediaRule | undefined' is not assignable to parameter of type 'CSSStyleRule | CSSMediaRule'.
      const index = getRuleIndex(cssRule, sheet);
      // Inserting a new media rule right after the old one.
      this.insertMediaRule(mediaRule, index + 1);
      deleteRule(sheet, index);
    }
  }

  clear() {
    this.cssRules.clear();
    clear(this.ownerNode);
  }
}

/**
 * CSSOMWriter is async, because we want to group all changes to CSSOM and
 * apply them in one go to avoid unnecessary overhead.
 * One gotcha with this approach is that you can't access computed value synchronously
 * after setting it, you have to use the `.scheduleCommit(callback)`.
 */
export class CSSOMWriter implements CSSWriter<HTMLStyleElement> {
  renderer: StyleSheetRenderer;
  staged: UpdateType[] = [];
  scheduler: Scheduler;

  constructor(node?: HTMLStyleElement) {
    this.scheduler = new Scheduler();
    this.renderer = new StyleSheetRenderer(node);
  }

  addRule(rule: AnyRuleType) {
    if (rule instanceof StyleRule) {
      this.staged.push({
        type: ADD_STYLE_RULE,
        args: [rule],
      });
      return;
    }

    if (rule instanceof MediaRule) {
      this.staged.push({
        type: ADD_MEDIA_RULE,
        args: [rule],
      });
    }
  }

  // TODO we should make the `rule` reference `parentRule`,
  // because this logic makes it fragile when you forget to  pass the parent while
  // iterating over children.
  removeRule(rule: AnyRuleType, parentRule?: MediaRule) {
    if (parentRule && rule instanceof StyleRule) {
      this.staged.push({
        type: REMOVE_STYLE_RULE_FROM_PARENT_RULE,
        args: [rule, parentRule],
      });
      return;
    }
    this.staged.push({
      type: REMOVE_RULE,
      args: [rule],
    });
  }

  // TODO
  // Should we throw here? `addDeclaration` in string writer adds a declaration
  // to the last added rule before the last curly is closed, which
  // doesn't fit the concept of CSSOM writer.
  addDeclaration(/*:: property: string, value: ValueType */) {}

  // TODO
  // We need a place where we check if the rule has actually changed
  // in our Rule instance, without reading from CSSOM.
  // Example:
  // const hasChanged = block.set('color', 'red');
  // if (hasChanged) writer.setProperty('color')
  // Alternatively we can maintain CSSOM values in CSSOM writer and check for a change here.
  setProperty(rule: StyleRule, property: LonghandId) {
    this.staged.push({
      type: SET_PROPERTY,
      args: [rule, property],
    });
  }

  removeProperty(rule: StyleRule, property: LonghandId) {
    this.staged.push({
      type: REMOVE_PROPERTY,
      args: [rule, property],
    });
  }

  setMediaQueries(rule: MediaRule) {
    this.staged.push({
      type: SET_MEDIA_QUERIES,
      args: [rule],
    });
  }

  addStyleRuleToParentRule(styleRule: StyleRule, parentRule: MediaRule) {
    this.staged.push({
      type: ADD_STYLE_RULE_TO_PARENT_RULE,
      args: [styleRule, parentRule],
    });
  }

  clear() {
    this.staged.push({type: CLEAR});
  }

  commit(): HTMLStyleElement {
    for (const update of this.staged) {
      switch (update.type) {
        case ADD_STYLE_RULE:
        case ADD_STYLE_RULE_TO_PARENT_RULE:
          // eslint-disable-next-line prefer-spread
          this.renderer.addStyleRule.apply(this.renderer, update.args);
          break;
        case ADD_MEDIA_RULE:
          // eslint-disable-next-line prefer-spread
          this.renderer.insertMediaRule.apply(this.renderer, update.args);
          break;
        case SET_PROPERTY:
          // eslint-disable-next-line prefer-spread
          this.renderer.setProperty.apply(this.renderer, update.args);
          break;
        case REMOVE_PROPERTY:
          // eslint-disable-next-line prefer-spread
          this.renderer.removeProperty.apply(this.renderer, update.args);
          break;
        case REMOVE_RULE:
        case REMOVE_STYLE_RULE_FROM_PARENT_RULE:
          // eslint-disable-next-line prefer-spread
          this.renderer.removeRule.apply(this.renderer, update.args);
          break;
        case SET_MEDIA_QUERIES:
          // eslint-disable-next-line prefer-spread
          this.renderer.setMediaQueries.apply(this.renderer, update.args);
          break;
        case CLEAR:
          this.renderer.clear();
          break;
        default:
          // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'never'.
          throw new Error(`Unhandled update type "${update.type}".`);
      }
    }
    this.staged.length = 0;
    return this.renderer.ownerNode;
  }

  scheduleCommit(callback?: (arg1: HTMLStyleElement) => void) {
    this.scheduler.next(() => {
      this.commit();
      if (callback) callback(this.renderer.ownerNode);
    });
  }
}
